import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { allNavItems } from '@/app/navigation';
import {
  members,
  permissions,
  roles,
  type Permission,
  type Role,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { Caixa } from './Caixa';
import { agruparPermissoes } from './gruposDePermissao';
import { PermissionMatrix } from './PermissionMatrix';

/**
 * Perfis de acesso: um quadro por perfil, como no OnPoint.
 *
 * Cada quadro mostra o nome do perfil, a secao "Usuarios" com quem o tem e os grupos
 * "Permissoes - ..." na ordem do OnPoint, so com o que o perfil concede. Os botoes
 * sao os do OnPoint: Adicionar perfil de acesso, Gerenciar usuarios do perfil,
 * Configurar perfil e Excluir perfil. A matriz de permissoes continua, dentro de
 * Configurar perfil.
 *
 * Os perfis sao os do SeguraCore ate a matriz de perfis da seguradora chegar
 * (CFG-002): o quadro mostra a estrutura do OnPoint sem inventar a matriz.
 */
export function RoleBoards() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeCriar = can('usuarios.papel.create');
  const podeExcluir = can('usuarios.papel.delete');
  const podeConfigurar = can('usuarios.papel.update');

  const [criando, setCriando] = useState(false);
  const [configurando, setConfigurando] = useState<Role | null>(null);
  const [gerindo, setGerindo] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<Role | null>(null);

  const listaPerfis = useQuery({
    queryKey: ['roles'],
    queryFn: ({ signal }) => roles.list(signal),
  });
  const catalogo = useQuery({
    queryKey: ['permissions', 'todas'],
    queryFn: ({ signal }) => permissions.list(signal),
  });

  const criar = useMutation({
    mutationFn: (input: { name: string; description: string }) => roles.create(input),
    onSuccess: (novo) => {
      toast.success(`Perfil ${novo.name} criado. Configure as permissoes dele.`);
      setCriando(false);
      setConfigurando(novo);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const excluir = useMutation({
    mutationFn: (roleId: string) => roles.remove(roleId),
    onSuccess: () => {
      toast.success('Perfil excluido.');
      setExcluindo(null);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setExcluindo(null);
    },
  });

  if (listaPerfis.isPending || catalogo.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (listaPerfis.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar os perfis">
        {listaPerfis.error.message}
      </Alert>
    );
  }

  // O perfil de acesso total vem primeiro, como o ADMIN no OnPoint; o resto pelo
  // nome.
  const perfis = [...(listaPerfis.data ?? [])].sort((a, b) =>
    a.grants_all === b.grants_all
      ? a.name.localeCompare(b.name)
      : a.grants_all
        ? -1
        : 1,
  );
  const descricoes = new Map<string, Permission>(
    (catalogo.data ?? []).map((item) => [item.key, item]),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600">
          O que cada perfil pode fazer e quem o tem. A decisao vale no servidor, a cada
          requisicao.
        </p>
        <div className="flex flex-wrap gap-2">
          {podeConfigurar && perfis.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setGerindo(perfis[0]?.id ?? null)}
            >
              Gerenciar usuarios do perfil
            </Button>
          ) : null}
          {podeCriar ? (
            <Button size="sm" onClick={() => setCriando(true)}>
              Adicionar perfil de acesso
            </Button>
          ) : null}
        </div>
      </div>

      {perfis.length === 0 ? (
        <Alert tone="info" title="Nenhum perfil cadastrado">
          Os perfis nascem com a empresa. Fale com o suporte se esta lista estiver
          vazia.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {perfis.map((perfil) => (
          <QuadroDoPerfil
            key={perfil.id}
            perfil={perfil}
            descricoes={descricoes}
            podeConfigurar={podeConfigurar}
            podeExcluir={
              podeExcluir &&
              !perfil.is_system &&
              !perfil.is_immutable &&
              !perfil.grants_all
            }
            onConfigurar={() => setConfigurando(perfil)}
            onExcluir={() => setExcluindo(perfil)}
          />
        ))}
      </div>

      {criando ? (
        <FormularioDePerfil
          salvando={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      ) : null}

      {configurando ? (
        <ConfigurarPerfilPorId
          lido={perfis.find((item) => item.id === configurando.id) ?? configurando}
          onClose={() => setConfigurando(null)}
        />
      ) : null}

      {gerindo ? (
        <UsuariosDoPerfil
          perfis={perfis}
          inicial={gerindo}
          onClose={() => setGerindo(null)}
        />
      ) : null}

      <ConfirmDialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        onConfirm={() => excluindo && excluir.mutate(excluindo.id)}
        title="Excluir perfil"
        message={
          excluindo
            ? `O perfil ${excluindo.name} deixa de existir nesta empresa. Um perfil ainda concedido a alguem nao e excluido: troque o perfil dessas pessoas antes.`
            : ''
        }
        confirmLabel="Excluir"
        tone="danger"
        loading={excluir.isPending}
      />
    </div>
  );
}

const publicos: Record<string, string> = {
  seguradora: 'Seguradora',
  corretora: 'Corretora',
  assessoria: 'Assessoria',
};

function QuadroDoPerfil({
  perfil,
  descricoes,
  podeConfigurar,
  podeExcluir,
  onConfigurar,
  onExcluir,
}: {
  perfil: Role;
  descricoes: Map<string, Permission>;
  podeConfigurar: boolean;
  podeExcluir: boolean;
  onConfigurar: () => void;
  onExcluir: () => void;
}) {
  const usuarios = useQuery({
    queryKey: ['roles', perfil.id, 'members'],
    queryFn: ({ signal }) => roles.members(perfil.id, signal),
  });

  const concedidas = perfil.permissions.map(
    (chave) =>
      descricoes.get(chave) ?? {
        key: chave,
        module: chave.split('.')[0] ?? '',
        resource: '',
        action: '',
        description: chave,
      },
  );
  const grupos = agruparPermissoes(concedidas);

  return (
    <Card>
      <CardHeader
        title={perfil.name}
        {...(perfil.description ? { description: perfil.description } : {})}
        action={
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{publicos[perfil.audience] ?? perfil.audience}</Badge>
            {perfil.is_default ? <Badge tone="brand">Padrao</Badge> : null}
            {perfil.is_assignable ? null : <Badge tone="warning">Nao atribuivel</Badge>}
          </div>
        }
      />
      <CardBody className="flex flex-col gap-4">
        <p className="text-2xs text-ink-500">
          Tela inicial: <span className="font-mono">{perfil.home_route || '/'}</span>
        </p>

        <section aria-label={`Usuarios do perfil ${perfil.name}`}>
          <h4 className="text-xs font-semibold text-ink-800">Usuarios</h4>
          {usuarios.isPending ? (
            <Skeleton className="mt-2 h-5 w-40" />
          ) : (usuarios.data ?? []).length === 0 ? (
            <p className="mt-1 text-2xs text-ink-500">Ninguem tem este perfil.</p>
          ) : (
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {(usuarios.data ?? []).map((pessoa) => (
                <li key={pessoa.id}>
                  <Badge tone="neutral">{pessoa.name}</Badge>
                </li>
              ))}
            </ul>
          )}
        </section>

        {perfil.grants_all ? (
          <p className="text-xs text-ink-700">
            Todas as permissoes do catalogo, inclusive as que forem criadas depois.
          </p>
        ) : grupos.length === 0 ? (
          <p className="text-xs text-ink-500">Nenhuma permissao concedida.</p>
        ) : (
          grupos.map((grupo) => (
            <section key={grupo.titulo}>
              <h4 className="text-xs font-semibold text-ink-800">{grupo.titulo}</h4>
              <ul className="mt-1 list-disc pl-5 text-2xs text-ink-600">
                {grupo.itens.map((item) => (
                  <li key={item.key}>{item.description || item.key}</li>
                ))}
              </ul>
            </section>
          ))
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-3">
          {podeExcluir ? (
            <Button size="sm" variant="ghost" onClick={onExcluir}>
              Excluir perfil {perfil.name}
            </Button>
          ) : null}
          {podeConfigurar ? (
            <Button size="sm" variant="secondary" onClick={onConfigurar}>
              Configurar perfil {perfil.name}
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

/** Telas que podem ser a inicial do perfil: as do menu do publico dele. */
function telasDoPublico(perfil: Role): { value: string; label: string }[] {
  const daCorretora = perfil.broker_scoped;
  const telas = allNavItems()
    .filter((item) =>
      daCorretora ? item.audience !== 'insurer' : item.audience !== 'broker',
    )
    .map((item) => ({ value: item.path, label: `${item.label} (${item.path})` }));
  if (!telas.some((tela) => tela.value === perfil.home_route)) {
    telas.push({ value: perfil.home_route, label: perfil.home_route });
  }
  return telas;
}

/**
 * Le o perfil pelo identificador (GET /roles/{id}) antes de configurar: o que se edita
 * e o que esta gravado agora, e nao a linha da lista. Se a leitura falhar, vale a da
 * lista, e o servidor confere de novo ao salvar.
 */
function ConfigurarPerfilPorId({ lido, onClose }: { lido: Role; onClose: () => void }) {
  const leitura = useQuery({
    queryKey: ['roles', lido.id],
    queryFn: ({ signal }) => roles.get(lido.id, signal),
  });
  if (leitura.isPending) {
    return (
      <Modal open onClose={onClose} title={`Carregando o perfil ${lido.name}`}>
        <Skeleton className="h-40 w-full" />
      </Modal>
    );
  }
  return <ConfigurarPerfil perfil={leitura.data ?? lido} onClose={onClose} />;
}

/**
 * Configurar perfil: nome, descricao, tela inicial, perfil padrao e perfil
 * atribuivel, e a matriz de permissoes.
 *
 * O perfil imutavel aceita so a tela inicial. O servidor recusa o resto, e a tela
 * nem oferece.
 */
function ConfigurarPerfil({ perfil, onClose }: { perfil: Role; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState(perfil.name);
  const [descricao, setDescricao] = useState(perfil.description);
  const [telaInicial, setTelaInicial] = useState(perfil.home_route || '/');
  const [padrao, setPadrao] = useState(perfil.is_default);
  const [atribuivel, setAtribuivel] = useState(perfil.is_assignable);

  const salvar = useMutation({
    mutationFn: () =>
      roles.update(
        perfil.id,
        perfil.is_immutable
          ? { home_route: telaInicial }
          : {
              name: nome,
              description: descricao,
              home_route: telaInicial,
              is_default: padrao,
              is_assignable: atribuivel,
            },
      ),
    onSuccess: (gravado) => {
      toast.success(`Perfil ${gravado.name} configurado.`);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Configurar perfil ${perfil.name}`}
      description="Os dados do perfil e o que ele permite."
      size="lg"
    >
      <div className="flex flex-col gap-6">
        <form onSubmit={enviar} className="flex flex-col gap-4">
          {salvar.error ? (
            <Alert tone="danger" title="Nao foi possivel configurar o perfil">
              {salvar.error.message}
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nome do perfil"
              required
              maxLength={60}
              disabled={perfil.is_immutable}
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
            />
            <Select
              label="Tela inicial"
              value={telaInicial}
              options={telasDoPublico(perfil)}
              onChange={(evento) => setTelaInicial(evento.target.value)}
            />
          </div>
          <Field
            label="Descricao"
            maxLength={500}
            disabled={perfil.is_immutable}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
          />
          <div className="flex flex-wrap gap-6">
            <Caixa
              label="Perfil padrao"
              checked={padrao}
              disabled={perfil.is_immutable}
              onChange={(marcado) => {
                setPadrao(marcado);
                if (marcado) setAtribuivel(true);
              }}
            />
            <Caixa
              label="Perfil atribuivel"
              checked={atribuivel}
              disabled={perfil.is_immutable || padrao}
              onChange={setAtribuivel}
            />
          </div>
          {perfil.is_immutable ? (
            <p className="text-2xs text-ink-500">
              Perfil imutavel: so a tela inicial pode ser trocada.
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Fechar
            </Button>
            <Button type="submit" loading={salvar.isPending}>
              Salvar perfil
            </Button>
          </div>
        </form>

        <section className="border-t border-line pt-4">
          <h3 className="mb-3 text-sm font-semibold text-ink-900">Permissoes</h3>
          <PermissionMatrix perfil={perfil} />
        </section>
      </div>
    </Modal>
  );
}

/**
 * Gerenciar usuarios do perfil: quem tem o perfil, com incluir e retirar.
 *
 * Incluir e retirar sao a concessao e a retirada avulsas de papel, com o teto de
 * concessao e a conferencia do ultimo administrador no servidor.
 */
function UsuariosDoPerfil({
  perfis,
  inicial,
  onClose,
}: {
  perfis: Role[];
  inicial: string;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [perfilID, setPerfilID] = useState(inicial);
  const [incluir, setIncluir] = useState('');

  const perfil = perfis.find((item) => item.id === perfilID) ?? perfis[0];

  const quemTem = useQuery({
    queryKey: ['roles', perfilID, 'members'],
    queryFn: ({ signal }) => roles.members(perfilID, signal),
  });
  const todos = useQuery({
    queryKey: ['members', { q: '', status: '' }],
    queryFn: ({ signal }) => members.list({}, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
    void queryClient.invalidateQueries({ queryKey: ['members'] });
  }

  const conceder = useMutation({
    mutationFn: (memberId: string) => members.assignRole(memberId, perfilID),
    onSuccess: () => {
      toast.success('Perfil concedido. Vale na proxima requisicao da pessoa.');
      setIncluir('');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const retirar = useMutation({
    mutationFn: (memberId: string) => members.revokeRole(memberId, perfilID),
    onSuccess: () => {
      toast.success('Perfil retirado. Vale na proxima requisicao da pessoa.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const comPerfil = new Set((quemTem.data ?? []).map((pessoa) => pessoa.id));
  const candidatos = (todos.data ?? []).filter(
    (pessoa) => !comPerfil.has(pessoa.id) && pessoa.user_id !== session?.user.id,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Gerenciar usuarios do perfil"
      description="Quem tem o perfil escolhido."
      footer={<Button onClick={onClose}>Concluir</Button>}
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Perfil"
          value={perfilID}
          options={perfis.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(evento) => setPerfilID(evento.target.value)}
        />

        {perfil && !perfil.is_assignable ? (
          <Alert tone="warning" title="Perfil nao atribuivel">
            Este perfil nao e concedido a mais ninguem. Quem ja o tem pode ser retirado.
          </Alert>
        ) : null}

        {quemTem.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (quemTem.data ?? []).length === 0 ? (
          <p className="text-sm text-ink-500">Ninguem tem este perfil.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {(quemTem.data ?? []).map((pessoa) => (
              <li
                key={pessoa.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink-800">
                    {pessoa.name}
                  </span>
                  <span className="block truncate text-2xs text-ink-500">
                    {pessoa.email}
                  </span>
                </span>
                {pessoa.user_id === session?.user.id ? (
                  <span className="text-2xs text-ink-400">voce</span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Retirar ${pessoa.name} do perfil`}
                    disabled={retirar.isPending}
                    onClick={() => retirar.mutate(pessoa.id)}
                  >
                    Retirar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {perfil?.is_assignable ? (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select
                label="Incluir pessoa"
                placeholder="Escolha a pessoa"
                value={incluir}
                options={candidatos.map((pessoa) => ({
                  value: pessoa.id,
                  label: `${pessoa.name} (${pessoa.email})`,
                }))}
                onChange={(evento) => setIncluir(evento.target.value)}
              />
            </div>
            <Button
              disabled={incluir === ''}
              loading={conceder.isPending}
              onClick={() => conceder.mutate(incluir)}
            >
              Incluir
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Formulario do perfil proprio da empresa.
 *
 * O perfil nasce sem permissao nenhuma e recebe as suas em Configurar perfil, que
 * abre logo depois de criar.
 */
function FormularioDePerfil({
  salvando,
  error,
  onCancel,
  onSubmit,
}: {
  salvando: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { name: string; description: string }) => void;
}) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    onSubmit({ name: nome, description: descricao });
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title="Adicionar perfil de acesso"
      description="Ele nasce sem permissao e recebe as suas em Configurar perfil."
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel criar o perfil">
            {error.message}
          </Alert>
        ) : null}

        <Field
          label="Nome do perfil"
          required
          maxLength={60}
          value={nome}
          hint="Como o perfil aparece na lista de pessoas."
          onChange={(evento) => setNome(evento.target.value)}
        />
        <Field
          label="Descricao"
          value={descricao}
          hint="Opcional. Explica para que serve o perfil."
          onChange={(evento) => setDescricao(evento.target.value)}
        />

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvando}>
            Criar perfil
          </Button>
        </div>
      </form>
    </Modal>
  );
}
